import { Component, EventEmitter, Input, Output } from '@angular/core';
import { EventsSegment } from '../events-list/events-list.component';

/**
 * Venue | City — the control above the events screen's two sections, the web twin of iOS's
 * `EventsSegmentControl` and Android's.
 *
 * ⚠️ **It is chrome, not content.** It renders ABOVE the error and empty states rather than inside
 * them, which is what cost iOS a whole device session (tracker **I-23**): with the control inside the
 * stack the empty state owns, a venue with no programme of its own — exactly the venue this feature
 * is most useful for — made the control unreachable. The web's empty state is laid out in flow rather
 * than as an overlay, so it never swallowed taps, but the same rule keeps the control on screen when
 * the venue half fails.
 *
 * Presentational only: it holds no state and fetches nothing. The parent decides whether it exists at
 * all (`showsCitySegment`), and both programmes are already loaded by the time it appears.
 */
@Component({
    selector: 'app-events-segment-control',
    template: `
    <div class="ev-segment" role="tablist" [attr.aria-label]="'events.title' | translate">
      <button type="button" class="ev-segment-btn" role="tab"
              [class.ev-segment-on]="segment === 'venue'"
              [attr.aria-selected]="segment === 'venue'"
              (click)="select('venue')">
        {{ 'events.segmentVenue' | translate }}
      </button>
      <button type="button" class="ev-segment-btn" role="tab"
              [class.ev-segment-on]="segment === 'city'"
              [attr.aria-selected]="segment === 'city'"
              (click)="select('city')">
        {{ 'events.segmentCity' | translate }}
      </button>
    </div>
  `,
    styles: [`
    :host { display: block; }
    .ev-segment {
      display: flex;
      gap: 4px;
      margin: 0 15px 12px;
      padding: 4px;
      border-radius: 14px;
      background: var(--ev-surface-raised, #252525);
    }
    .ev-segment-btn {
      flex: 1 1 0;
      /* Equal halves whatever the words are: "Établissement" must not squeeze "Ville" out. */
      min-width: 0;
      min-height: 40px;
      padding: 8px 12px;
      border: 0;
      border-radius: 10px;
      background: transparent;
      color: var(--ev-on-surface-variant, #CFCFCF);
      font-size: 15px;
      font-weight: 600;
      cursor: pointer;
      overflow: hidden;
      text-overflow: ellipsis;
      white-space: nowrap;
      transition: background-color 120ms ease, color 120ms ease;
    }
    .ev-segment-on {
      background: #FFFFFF;
      color: #0C0C0C;
    }
  `],
    standalone: false
})
export class EventsSegmentControlComponent {
  @Input() segment: EventsSegment = 'venue';

  @Output() segmentChange = new EventEmitter<EventsSegment>();

  select(segment: EventsSegment): void {
    if (segment === this.segment) return;
    this.segmentChange.emit(segment);
  }
}
